// @ts-nocheck
import { ApiParameter } from "@insite/client-framework/Services/ApiService";
import { useToaster } from "@insite/mobius/Toast/ToasterContext";

import {
    loadBudgetDetail,
    loadBudgetDetailView,
    loadBudgetSummary,
    loadExpenditureByProduct,
    loadExpenditurebyPurchaser,
    loadExpenditures,
    loadExpendituresByManufacturers,
    loadInvoiceDetail,
    loadInvoiceDetails,
    loadInvoiceSummary,
    loadManufacturers,
    loadMonthlyExpenseComparison,
    loadPoPicker,
    loadReportFrequency,
    loadServiceHistory,
    loadServiceHistoryInvoice,
} from "@store/Framework/Common/Hooks/useReportAPI/ReportApiReportHandlers";
import {
    BudgetDetailModel,
    BudgetDetailViewModel,
    BudgetSummaryModel,
    ExpenditureByProductModel,
    ExpenditureByPurchaserModel,
    ExpendituresModel,
    InvoiceDetailModel,
    InvoiceDetailsModel,
    InvoiceSummaryModel,
    ManufacturersPickerModel,
    MonthlyExpenseComparisonModel,
    PoPickerModel,
    ReportOrderFrequencyModel,
    ServiceHistoryInvoiceModel,
    ServiceHistoryModel,
} from "@store/Framework/Services/Reporting/ReportingModels";
import { postApiData } from "@store/Framework/Services/Reporting/ReportingService";
import {
    ExpenditureByProductParameters,
    ExpendituresByManufacturersParameters,
    ExpendituresParameters,
    GetReportOrderFrequencyApiParameter,
    InvoiceDetailParameters,
    InvoiceDetailsParameters,
    ManufacturersPickerParameters,
    MonthlyExpenseComparisonParameters,
    ServiceHistoryInvoiceParameters,
    ServiceHistoryParameters,
} from "@store/Framework/Types/types";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import isApiError from "@insite/client-framework/Common/isApiError";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import {
    isBudgetSummaryError,
    isExpendituresByManufacturersError,
    isExpendituresError,
    isInvoiceSummaryError,
    ismonthlyExpenseComparisonError,
    isPoPickerError,
    isReportFrequencyError,
} from "@store/Framework/Common/Hooks/useReportAPI/reportApiErrorHandlers";
import { usePoError } from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/usePoError";
import { useValidationError } from "@store/Framework/Store/Bunzl/Pages/Reporting/StoreHooks/useValidationError";
import { set } from "js-cookie";
import { useState } from "react";
import { useDispatch } from "react-redux";

const reportHandlers = {
    expendituresByManufacturers: loadExpendituresByManufacturers,
    expenditureByPurchaser: loadExpenditurebyPurchaser,
    expenditureByProduct: loadExpenditureByProduct,
    expenditure: loadExpenditures,
    budgetDetailView: loadBudgetDetailView,
    budgetDetail: loadBudgetDetail,
    monthlyExpenseComparison: loadMonthlyExpenseComparison,
    orderFrequency: loadReportFrequency,
    invoiceSummary: loadInvoiceSummary,
    manufacturers: loadManufacturers,
    invoice: loadInvoiceDetail,
    invoices: loadInvoiceDetails,
    invoiceSummaryPoPicker: loadPoPicker,
    budgetSummary: loadBudgetSummary,
    serviceHistory: loadServiceHistory,
    serviceHistoryInvoice: loadServiceHistoryInvoice,
};

export type ApiReturnTypes =
    | ExpenditureByPurchaserModel
    | ExpenditureByProductModel
    | ExpendituresModel
    | BudgetDetailViewModel
    | MonthlyExpenseComparisonModel
    | ReportOrderFrequencyModel
    | InvoiceSummaryModel
    | InvoiceDetailModel
    | PoPickerModel
    | BudgetSummaryModel
    | BudgetDetailModel
    | ServiceHistoryModel
    | ServiceHistoryInvoiceModel
    | ManufacturersPickerModel
    | InvoiceDetailsModel;

export type ApiParameters =
    | ExpenditureByProductParameters
    | ExpendituresParameters
    | GetReportOrderFrequencyApiParameter
    | InvoiceDetailParameters
    | InvoiceDetailsParameters
    | ServiceHistoryParameters
    | ServiceHistoryInvoiceParameters
    | MonthlyExpenseComparisonParameters
    | ManufacturersPickerParameters
    | ExpendituresByManufacturersParameters;

export type ReportTypes = keyof typeof reportHandlers;
// no need to validate invoiceDetail,serviceHistory, and budgets as all data is required

const validationFunctions = {
    invoices: (apiParameters: ApiParameter) => true,
    expendituresByManufacturers: isExpendituresByManufacturersError,
    manufacturers: (apiParameters: ApiParameters) => true,
    expenditureByPurchaser: isReportFrequencyError,
    expenditureByProduct: isReportFrequencyError,
    invoiceSummary: isInvoiceSummaryError,
    serviceHistoryInvoice: (apiParameters: ApiParameters) => true,
    orderFrequency: isReportFrequencyError,
    invoice: (apiParameters: ApiParameters) => true,
    invoiceSummaryPoPicker: isPoPickerError,
    budgetSummary: isBudgetSummaryError,
    expenditure: isExpendituresError,
    budgetDetail: (apiParameters: ApiParameters) => true,
    budgetDetailView: (apiParameters: ApiParameters) => true,
    monthlyExpenseComparison: ismonthlyExpenseComparisonError,
    serviceHistory: (apiParameters: ApiParameters) => true,
};

/**
 *
 * @param type {string} - type of report to load
 * @returns {data, isLoading, error, updateData} - data is the data returned from the api, isLoading is a boolean indicating if the api is loading, error is a boolean indicating if there was an error
 * updateData is a function to update the data
 */

const useReportApi = (type: ReportTypes) => {
    const signInUrl = useAppSelector(state => getPageLinkByPageType(state, "SignInPage")?.url);
    const currentLocation = useAppSelector(state => getLocation(state));
    const history = useHistory();
    const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
    const toast = useToaster();
    const [poError, setPoError] = usePoError();
    const dispatch = useDispatch();
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isError, setIsError] = useValidationError();

    /**
     * @param apiParameters - parameters to pass to the api call
     * @returns {Promise} - Promise Object that returns an APiReturnTypes object or null if there was a problem with the call
     */
    const updateData = async (apiParameters: ApiParameters): Promise<ApiReturnTypes | null> => {
        if (validationFunctions[type](apiParameters)) {
            setLoading(true);
            if (isError) {
                setIsError(false);
            }
            if (type === "invoiceSummaryPoPicker") {
                if (poError) {
                    setPoError(false);
                }
            }
            try {
                setHasSubmitted(true);
                const apiResult = await postApiData({ reportName: type, ...apiParameters });
                setLoading(false);
                if (reportHandlers[type](dispatch, apiResult)) {
                    return apiResult;
                }

                return null;
            } catch (error) {
                setLoading(false);

                if (error instanceof Error) {
                    if (isApiError(error) && (error.status === 401 || error.status === 403)) {
                        history.push(`${signInUrl}?returnUrl=${currentLocation.pathname}`);
                        return null;
                    }

                    toast.addToast({
                        body: translate(`An Error has Occurred. Please try again.`),
                        messageType: "warning",
                    });
                }
            }
            return null;
        }
        if (type === "invoiceSummaryPoPicker") {
            setPoError(true);
        } else {
            setIsError(true);
        }
        return null;
    };

    return {
        poError,
        hasSubmitted,
        setHasSubmitted,
        setIsError,
        setLoading,
        isLoading,
        updateData,
        isError,
    };
};

export default useReportApi;
